<!--搜索结果显示表格-->
<template>
  <div
    ref="containerRef"
    class="st-cont"
    :listStyle="listStyle"
    :freeSelect="freeSelect"
    :cellAlignTop="cellAlignTop"
    :masterDetail="enableMasterDetail"
  >
    <CmdBar v-if="showConfig === true">
      <template #right>
        <TableConfigButton />
      </template>
    </CmdBar>

    <AgGridVue
      ref="tableRef"
      :key="tableKey"
      v-loading.lock="loading"
      v-bind="moreCloned"
      element-loading-background="#ffffff66"
      :style="tableStyle"
      :row-data="tableData"
      :column-defs="lastColDefs"
      :default-col-def="defaultColDef"
      :row-selection="rowSelection"
      :header-height="headerHeight"
      :row-height="rowHeight"
      :enable-cell-text-selection="freeSelectText"
      :suppress-row-click-selection="suppressRowClickSelection"
      :row-multi-select-with-click="rowMultiSelectWithClick"
      :suppress-drag-leave-hides-columns="suppressDragLeaveHidesColumns"
      :locale-text="localeText"
      :no-rows-overlay-component="noRowsOverlayComponent"
      :prevent-default-on-context-menu="true"
      :suppress-loading-overlay="true"
      :suppress-no-rows-overlay="!noRowsOverlayComponent"
      multi-sort-key="ctrl"
      :class="{
        'ag-theme-balham': true,
        'easy-table': true,
        'auto-rowh': autoHeight,
        'ag-with-header-group': hasHeaderGroup
      }"
    >
    </AgGridVue>

    <div class="footer">
      <div class="slot">
        <span v-if="selectRowCount" class="pagination_slot_box">
          已选&nbsp;
          <span style="color: orange">{{ selectRowCount }} </span>
          &nbsp;条&nbsp;&nbsp;&nbsp;
          <span
            v-if="!disableSelectionCancel && selectRowCount > 1"
            class="cancel_checked_lines"
            @click="cancelSelect"
          >
            取消选择&nbsp;&nbsp;
          </span>
        </span>
        <slot name="footer" />
      </div>
      <ElPagination
        v-if="paginationData"
        v-model:page-size="paginationData.size"
        v-model:current-page="paginationData.page"
        background
        layout="slot"
        :total="totalRows"
        :pager-count="5"
        :page-sizes="pageSizes"
        @size-change="submitSearch"
        @current-change="submitSearch"
      >
        <SearchTablePagination
          v-model:page-size="paginationData.size"
          v-model:current-page="paginationData.page"
          :page-sizes="pageSizes"
          :total="totalRows"
          @size-change="submitSearch"
          @current-change="submitSearch"
        />
      </ElPagination>
    </div>

    <AgGridTooltip
      v-if="!disableOverflowTip"
      :virtual-ref="virtualPopover"
      :tooltip-text="tooltipContext"
    />
  </div>
</template>

<script setup lang="ts">
/**
 * ### 查询表格组件
 */
import axios, { CanceledError } from 'axios';
import { ref, shallowRef, computed, onBeforeMount } from 'vue';
import { promiseTimeout, useTimeoutFn } from '@vueuse/core';
import { vLoading, ElLoading } from 'element-plus';
import { AgGridVue } from 'ag-grid-vue3';
import { useShareData } from '../core/SharedData';
import { useAgGridExt, localeText } from '../common/tab/AgGridExt';
import { useAgGridToolTip } from '../common/tab/AgGridTooltip';
import {
  MasterDetailDef,
  useMasterDetails
} from '../common/tab/MasterDetails.tsx';
import { useSummaryRow } from '../common/tab/SummaryRow.tsx';
import SearchTablePagination from './internal/SearchTablePagination.vue';

import { PlainObject } from '../core/types';

import {
  GridApi,
  ColDef,
  GridOptions,
  ColumnApi,
  GetRowIdParams
} from 'ag-grid-community';

import { showError } from '../core/Common';
import { Deferred, createDeferred } from '../core/PromiseUtil';
import { printDom } from '../core/PrintDom';

import { getTableAnnotations } from '../core/TableUtilInternal';

import { getLayout, saveLayout } from '../core/LayoutPersistent';

import { Pagination } from './types';

import { useComplexColumns } from '../common/tab/ComplexColumn';

import CmdBar from '../common/cmd/CmdBar.vue';
import TableConfigButton from '../common/table-config/TableConfigButton.vue';
import {
  TableContextMenu,
  useContextMenu
} from '../common/tab/AgContextMenu.tsx';
import AgGridIndexComponent from '../common/tab/AgGridIndexComponent';
import AgGridTooltip from '../common/tab/AgGridTooltip.vue';

import { getNoRowsOverlayComponent } from '../common/tab/AgNoRowsOverlayComponent';
import { PaginationResult, getApiProtocol } from '../core/ApiProtocol';
import { useTableColumnSetting } from '../common/tab/TableSettingUtil';

import _ from 'lodash-es';

// 属性定义
const props = withDefaults(
  defineProps<{
    /**
     * ### 表格数据查询url
     */
    url: string;
    /**
     * ### 传入表格数据
     * <warning> 仅当未传入url时有效
     */
    data?: () => PlainObject[];

    /**
     * ### 表格列定义 也可以传入一个类
     */
    // eslint-disable-next-line @typescript-eslint/ban-types
    columnDefs: Function;
    /**
     * ### 表格列默认配置
     */
    defaultColDef?: ColDef;
    /**
     * ### 表格内容右键菜单设置
     */
    contextMenu?: TableContextMenu<any>;
    /**
     * ### 是否显示分页信息
     */
    pagination?: boolean;
    /**
     * ### 是否显示行序列号
     */
    seq?: boolean;
    /**
     * ### 行选择模式 单选/多选
     */
    rowSelection?: 'single' | 'multiple';
    /**
     * ### 点击行的时候是否自动选择
     */
    suppressRowClickSelection?: boolean;
    /**
     * ### 多行选择的时候, 点击行，是否取消之前已经选择的行
     */
    rowMultiSelectWithClick?: boolean;
    /**
     * ### 是否能够自由选择单元格文本
     */
    freeSelectText?: boolean;
    /**
     * ### 表头行高设置
     */
    headerHeight?: number;
    /**
     * ### 表内容行高设置
     * <warning> 当设置autoheight后 如果计算得出的行高低于该指定的值时 会取该项指定的值
     */
    rowHeight?: number;
    /**
     * ### 是否显示底部“取消选择”操作
     */
    disableSelectionCancel?: boolean;
    /**
     * ### 禁用文本overflow时的tip
     */
    disableOverflowTip?: boolean;
    /**
     * ### 简单列表模式, 不显示表头, 没有边框
     */
    listMode?: boolean;
    /**
     * ### 分组模式设置
     */
    masterDetailDef?: MasterDetailDef<any, any>;
    /**
     * ### 单元格是否居顶显示，否则居中
     */
    cellAlignTop?: boolean;
    /**
     * ### 无数据默认消息
     */
    noRowsMessage?: string;
    /**
     * ### 是否显示表头配置按钮
     * contextmenu 为表头右键展示配置项
     */
    showConfig?: 'contextmenu' | true | false;
    /**
     * ### 设置每页列表条数配置项
     * <error> 当前已废弃 固定死了 [50, 200, 500, 1000, 2000]
     */
    pageSizes?: number[]; // 分页大小

    // 表格style
    //tableStyle?: CSSProperties,

    /**
     * ### 双击行的回调函数
     */
    dblClickHandle?: string;
    /**
     * ### 更多配置 会将该配置原封不动的放入到ag-table中
     */
    more?: GridOptions<any>;
    /**
     * ### 扩展数据
     * <warning> SearchTable自己不用
     */
    extension?: Record<string, any>;
  }>(),

  // 属性默认值
  {
    pagination: true, // 默认为分页查询
    seq: true, // 显示行号

    headerHeight: 35,
    rowHeight: 32, // 行高, 默认32

    showConfig: 'contextmenu'

    // pageSizes: () => [50, 500, 1000, 3000]
  }
);

// 表格这空的显示的内容
const noRowsOverlayComponent = getNoRowsOverlayComponent(props.noRowsMessage);

// 分页写死 暂时不再支持配置
const pageSizes = [50, 200, 500, 1000, 2000];

// 复制一份more参数, 修改后绑定到组件上
const moreCloned: GridOptions<any> = props.more ? _.clone(props.more) : {};
if (moreCloned) {
  moreCloned.getRowId = undefined;
  moreCloned.suppressRowClickSelection = undefined;
  moreCloned.rowMultiSelectWithClick = undefined;
}

const tableKey = ref(0);
// 暴露方法
defineExpose({
  /**
   * ### 获取完整ag-table api方法
   * <error> 将下线
   */
  agApi: () => agApi,
  /**
   * ### 获取完整ag-table api方法
   */
  api: () => agApi,
  /**
   * ### 获取当前表格数据方法
   * <warning> 对返回的数组进行数组本身的修改时会触发表格数据渲染的更新
   */
  data: () => tableData.value,
  /**
   * ### 表格重新渲染方法
   */
  redraw: () => ++tableKey.value,
  /**
   * ### 打印接口
   */
  print: print,
  /**
   * ### 获取总条数方法
   */
  totalRows: () => totalRows.value,
  /**
   * ### 刷新汇总行方法
   */
  refreshSummaryRow: () => refreshSummaryRow()
});

// 没有列配置入口, 禁止列隐藏
// 仅有通过右键设置表头的配置也不拖拽隐藏列
// 因为用户未必知道右键可以设置列
const suppressDragLeaveHidesColumns =
  props.showConfig !== true &&
  !useShareData('table', 'setting').showColumnConfig;

// 分页参数
let paginationData = ref<Pagination>();

// ag-grid
let agApi: GridApi<any>;
let columnApi: ColumnApi;

const searchTable = useShareData('search', 'table');

let abortController: AbortController | undefined;
let pendingRequest: PlainObject | undefined;
const pendingDeferreds: Deferred<void>[] = [];

async function sendRequest(sp: PlainObject) {
  try {
    const rows = preprocess(await fetchTableData(sp));
    refreshSummaryRow(rows);
    tableData.value = rows;

    return true;
  } catch (e) {
    if (e instanceof CanceledError) {
      if (pendingDeferreds.length) {
        // 请求被保持loading状态
        loading.value = true;
      }
    } else {
      console.error('UnknownError', e);
    }
  }

  return false;
}

// 延时请求
const { start: sendDelayRequest } = useTimeoutFn(
  () => {
    if (!pendingRequest) return;

    const sp = pendingRequest;
    pendingRequest = undefined;

    // 通知执行结束
    sendRequest(sp).then((result) => {
      if (result) {
        pendingDeferreds.forEach((p) => p.resolve(undefined));

        // 清空
        pendingDeferreds.splice(0);
      }
    });
  },
  500,
  {
    immediate: false
  }
);

// 注册查询函数
searchTable.$('execute')((sp: PlainObject): Promise<void> => {
  // 当前正要执行请求,
  if (abortController) {
    abortController.abort();
  }

  if (abortController || pendingDeferreds.length) {
    abortController = undefined;

    // 保存请求
    pendingRequest = _.cloneDeep(sp);

    // 排队(后面通知执行结束)
    const deferred = createDeferred<void>();
    pendingDeferreds.push(deferred);

    // 启动延时请求
    sendDelayRequest();

    return deferred;
  }

  return sendRequest(sp).then(_.noop);
});

// searchPanel名
const searchName = useShareData('search', 'name');
if (props.pagination) {
  paginationData.value = useShareData('search', 'pagination') as Pagination;

  // 加载分页大小
  paginationData.value.size = getLayout(
    'search',
    ['pageSize', searchName],
    50,
    _.isInteger
  );
}

// 提交查询命令
let submitFunction = useShareData('search', 'submit');
async function submitSearch() {
  // 小于等于2000时自动保存分页大小
  if (paginationData.value && paginationData.value.size <= 2000) {
    saveLayout('search', ['pageSize', searchName], paginationData.value.size);
  }

  await submitFunction(true);
}

// 总条数
const totalRows = ref(0);

// 表格数据
const tableData = shallowRef<Readonly<PlainObject[]>>();

// 显示loading
const loading = ref(false);

function initLocalData(data: any) {
  // 增加一个临时key, 用于前端缓存数据
  for (let item of data) {
    item['_'] = {};
  }
}

const apiProtocol = getApiProtocol();
async function fetchTableData(searchParams?: PlainObject) {
  clearSelection();

  const url = props.url;
  // url未指定
  if (!url) {
    return props.data?.() || [];
  }

  loading.value = true;
  let paginationParams = pagnationParams();

  let resp: PaginationResult;
  try {
    abortController = new AbortController();
    resp = await apiProtocol.paginationRequest(
      axios,
      url,
      searchParams,
      paginationParams,
      { signal: abortController!.signal }
    );
  } catch (e) {
    if (e instanceof CanceledError) {
      // 抛出去, 让loading在取消期间保持
      throw e;
    }
    showError(e);
    return [];
  } finally {
    abortController = undefined;
    loading.value = false;

    // 取消选择, 否则ag-grid会记住之前的选择
    agApi?.deselectAll();
  }

  // 分页模式下
  if (props.pagination) {
    // 小于0表示服务端没和重算, 保持原值
    if (resp.totalCount >= 0) {
      totalRows.value = resp.totalCount;
    }

    if (paginationData.value && paginationData.value.page < 1) {
      paginationData.value.page = 1;
    }
  }

  const data = resp.data || [];
  // 增加一个临时key, 用于前端缓存数据
  initLocalData(data);

  return data;
}

// 分页header
function pagnationParams() {
  if (!paginationData.value) {
    return undefined;
  }

  //let showTotal = paginationData.value.showTotal ? 1 : 0;
  let pageNo = Math.max(paginationData.value.page, 1);

  return {
    currentPage: pageNo,
    pageSize: paginationData.value.size!
  };
}

// 处理列定义
let realColDefs: ColDef<any>[]; //
{
  const { fields, rowId } = getTableAnnotations(props.columnDefs);
  realColDefs = fields;
  if (rowId) {
    moreCloned.getRowId = (params: GetRowIdParams<PlainObject>) => {
      return '' + params.data[rowId];
    };
  }
}

// 是否包含自动高度的
const autoHeight = !!realColDefs.find((e) => e.autoHeight);

// 复制一份, 因为下面要修改数组
realColDefs = _.cloneDeep(realColDefs);

// 处理组合列
useComplexColumns('search', searchName, realColDefs, !props.listMode);

// 添加行号列
if (props.seq) {
  realColDefs.unshift({
    field: '__seq',
    headerName: '序号',
    sortable: false,
    maxWidth: 53,
    pinned: 'left',
    suppressMovable: true,
    cellStyle: { textAlign: 'right' },
    cellRenderer: AgGridIndexComponent
  });
}

if (props.rowSelection) {
  realColDefs.unshift({
    field: '__checkbox',
    headerName: '',
    maxWidth: 45,
    pinned: 'left',
    suppressMovable: true,
    checkboxSelection: true,
    showDisabledCheckboxes: true,
    headerCheckboxSelection: props.rowSelection !== 'single',
    cellClass: 'st-first-check-col',
    sortable: false
  });
}

// 分组处理
const { enableMasterDetail, preprocess } = useMasterDetails(
  realColDefs,
  moreCloned,
  props.masterDetailDef,
  () => ++tableKey.value
);

// 汇总行
const { refresh: refreshSummaryRow } = useSummaryRow(realColDefs, moreCloned);

const { lastColDefs, tableCtx } = useTableColumnSetting({
  realColDefs,
  getColumnApi() {
    return columnApi;
  },
  getAgApi() {
    return agApi;
  },
  tableName: searchName,
  registerApi: true,
  moreCloned
});

// 给列表配置提供api
if (tableCtx) {
  // 用于通用导出
  tableCtx.$('url')(props.url);
}

// 列的默认认设置
const defaultColDef: ColDef = props.defaultColDef ?? {
  sortable: true,
  unSortIcon: true,
  lockPinned: true,
  resizable: true,
  cellStyle: function (params) {
    if (
      typeof params.value === 'number' &&
      !params.column.getColDef().cellRenderer
    ) {
      return { textAlign: 'right' };
    } else {
      return null;
    }
  }
};

// 选中的行数
const selectRowCount = ref(0);
const onRowSelected = moreCloned.onRowSelected;
moreCloned.onRowSelected = (event) => {
  let count = 0;
  event.api.forEachNode(
    (node) => (count += node.isSelected() && !node.data?.['__uncount'] ? 1 : 0)
  );
  selectRowCount.value = count;

  onRowSelected?.(event);
};

// 取消选择
function cancelSelect() {
  agApi.deselectAll();
}

const onSortChanged = moreCloned.onSortChanged;
moreCloned.onSortChanged = (event) => {
  if (props.seq) {
    // 刷新行号
    agApi.forEachNodeAfterFilterAndSort((node) => {
      node.setDataValue('__seq', node.rowIndex! + 1);
    });
  }

  onSortChanged?.(event);
};

const onGridReady = moreCloned.onGridReady;
moreCloned.onGridReady = (event) => {
  agApi = event.api;
  columnApi = event.columnApi;

  onGridReady?.(event);
};

const onVirtualRowRemoved = moreCloned.onVirtualRowRemoved;
moreCloned.onVirtualRowRemoved = (event) => {
  updateSelection();
  onVirtualRowRemoved?.(event);
};

const onVirtualColumnsChanged = moreCloned.onVirtualColumnsChanged;
moreCloned.onVirtualColumnsChanged = (event) => {
  updateSelection();
  onVirtualColumnsChanged?.(event);
};

const tableStyle = `--ag-row-height: ${props.rowHeight - 6}px`;

// 自由选择单元格文字
const freeSelect = props.freeSelectText ? '' : undefined;

// 简单列表模式
const listStyle = props.listMode ? '' : undefined;

// 单元格内容居顶
const cellAlignTop = props.cellAlignTop ? '' : undefined;

// range selection支持
const { tableRef, clearSelection, updateSelection } = useAgGridExt(
  { disabled: props.freeSelectText },
  moreCloned
);

// 右键菜单
useContextMenu(tableRef, props, moreCloned, { submit: submitSearch });

// tooltip 支持
const virtualPopover = ref<HTMLElement>();
const { tooltipContext } = useAgGridToolTip(tableRef, virtualPopover);

// 是否有表头分组
const hasHeaderGroup = computed(
  () => !!lastColDefs.value.find((e: any) => e.children && e.children.length)
);

// 打印
const containerRef = ref();
async function print(options?: { title?: string; styles?: string }) {
  const domLayout = moreCloned.domLayout;

  // 显示loading
  let loadingInstance = ElLoading.service({
    text: '正在准备打印...',
    fullscreen: true,
    background: '#ffffff33'
  });

  async function closeLoading() {
    if (loadingInstance) {
      loadingInstance.close();
      loadingInstance = undefined as any;

      await promiseTimeout(100);
    }
  }

  try {
    // 切换到打印模式
    agApi.setDomLayout('print');
    await promiseTimeout(2000);

    await printDom(containerRef.value, {
      ...(options || {}),
      loaded: closeLoading
    });
  } finally {
    agApi.setDomLayout(domLayout);
    closeLoading();
  }
}

onBeforeMount(() => {
  // 关闭前如果还在查询, 则终止请求
  if (abortController) {
    abortController.abort();
    abortController = undefined;
  }
});
</script>

<style lang="scss" scoped>
/* https://ag-grid.com/angular-data-grid/global-style-customisation-variables/ */
.st-cont {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  background-color: white;
  margin: 0px var(--easy-page-padding-x);

  .footer {
    display: flex;
    height: 48px;
    padding: 0 12px;
    justify-content: center;
    user-select: none;

    .slot {
      height: 100%;
      flex-grow: 1;
      padding: 0 12px;
      display: flex;
      align-items: center;
    }

    :deep(.el-pagination) {
      --el-pagination-button-height: 26px;

      overflow-x: hidden;
    }
  }
}

.easy-table {
  --ag-font-size: 14px;
  --ag-border-color: #e6e9ef;
  --ag-row-hover-color: #f5f6f8;
  --ag-cell-horizontal-border: 1px solid #e6e9ef;
  --ag-cell-widget-spacing: 0;
  --ag-header-foreground-color: #333;
  --ag-header-background-color: #f5f6f8;
  --ag-header-column-separator-height: 100%;
  --ag-selected-row-background-color: #edf4ff;
  --ag-range-selection-border-color: #327de8;

  flex-grow: 1;
  padding: 0 12px;

  // fix erp2
  :deep(.ag-root-wrapper-body) {
    border-right-width: 0px;

    .ag-header {
      border-radius: 0px;
    }
  }

  :deep(.ag-root-wrapper) {
    border-radius: 2px 2px 0 0;
  }

  :deep(.ag-cell-wrapper) {
    display: flex;
    align-items: center;
    min-height: 100%;
    height: unset;
    width: 100%;
  }

  :deep(.ag-cell-auto-height .ag-cell-wrapper) {
    //确保autoHeight收缩时迅速
    min-height: unset !important;
  }

  :deep(.ag-cell-auto-height.ag-cell-wrap-text .ag-cell-wrapper) {
    position: relative;
    top: 50%;
    transform: translateY(-50%);
  }

  :deep(.ag-cell-wrapper.ag-row-group),
  :deep(.ag-cell-inline-editing .ag-cell-wrapper) {
    align-items: flex-start;
  }

  :deep(.ag-row) {
    line-height: var(--ag-row-height) !important;
  }

  :deep(.show-cell) {
    background-color: white;
    border-bottom: 1px solid #eee !important;
  }

  :deep(.ag-sort-indicator-icon) {
    color: rgba(51, 51, 51, 0.54);
  }

  :deep(.ag-pinned-right-header),
  :deep(.ag-pinned-right-cols-container) {
    box-shadow: #0000000f -4px 3px 4px;
  }

  :deep(.ag-selection-checkbox) {
    height: var(--ag-row-height);
  }

  :deep(.ag-cell) {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    line-height: var(--ag-row-height) !important;
  }

  //fix erp2
  //   &.ag-theme-balham {
  //     :deep(.ag-ltr) {
  //       .ag-cell {
  //         //fix erp2
  //         //border-right-color: #eee !important;
  //       }
  //     }
  //   }
}

.auto-rowh {
  :deep(.ag-cell).ag-cell-value > span {
    position: absolute;
    top: calc(50% - 2px);
    transform: translateY(-50%);
  }

  :deep(.ag-cell-wrapper) > .ag-cell-value {
    padding-bottom: 4px;
    line-height: normal;
  }
}

.st-cont[listStyle] {
  padding-top: 12px;

  .easy-table {
    --ag-range-selection-border-color: transparent !important;
    --ag-row-hover-color: transparent;

    :deep(.ag-header) {
      display: none;
    }

    :deep(.ag-center-cols-viewport) {
      overflow: hidden;
    }

    :deep(.ag-root-wrapper) {
      border: none;
    }

    :deep(.ag-row) {
      border-bottom-style: none;
    }

    :deep(.ag-row-hover) {
      background-color: transparent;
    }

    :deep(.ag-cell) {
      border-right-style: none;
    }
  }
}

.st-cont[freeSelect] {
  .easy-table {
    --ag-range-selection-border-color: #e6e9ef;

    :deep(.ag-cell) {
      line-height: normal !important;
    }
  }
}

.st-cont[cellAlignTop] {
  :deep(.ag-cell-wrapper) {
    padding-top: 8px;
    padding-bottom: 8px;
    align-items: baseline !important;
  }
}

.st-cont[masterDetail] {
  :deep(.ag-row) {
    // 还原
    overflow: visible !important;
  }

  :deep(.ag-header-cell[col-id='__m']) {
    display: none;
  }

  :deep(.ag-cell[col-id='__m']) {
    border: none;
  }

  :deep(:not(.show-cell) .ag-cell-wrapper) {
    box-sizing: border-box;
    height: 100% !important;
    overflow: hidden !important;
  }
}

.pagination_slot_box {
  vertical-align: middle;
  padding-left: 8px;
}

.cancel_checked_lines {
  font-size: 12px;
  color: #02adf1;
  cursor: pointer;
}
</style>
